import { Check, AlertTriangle, Repeat } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { ProcessedTransaction } from '@/hooks/use-import-statement';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { CategorySelect } from '@/components/shared/CategorySelect';

interface TransactionPreviewTableProps {
  transactions: ProcessedTransaction[];
  onToggle: (id: string) => void;
  onCategoryChange: (id: string, categoryId: string, categoryName: string) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  onDeselectDuplicates: () => void;
}

export function TransactionPreviewTable({
  transactions,
  onToggle,
  onCategoryChange,
  onSelectAll,
  onDeselectAll,
  onDeselectDuplicates
}: TransactionPreviewTableProps) {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    try {
      return format(parseISO(dateString), 'dd MMM', { locale: ptBR });
    } catch {
      return dateString;
    }
  };

  const selectedCount = transactions.filter(t => t.selected).length;
  const duplicatesCount = transactions.filter(t => t.is_duplicate).length;

  const getConfidenceBadge = (confidence: 'high' | 'medium' | 'low', autoMapped: boolean) => {
    if (!autoMapped) return null;
    
    const configs = {
      high: { label: 'Auto', className: 'bg-success/20 text-success border-success/30' },
      medium: { label: 'Sugerido', className: 'bg-warning/20 text-warning border-warning/30' },
      low: { label: 'Baixa', className: 'bg-muted text-muted-foreground border-border' }
    };
    
    const config = configs[confidence];
    return (
      <Badge variant="outline" className={cn('text-xs ml-2', config.className)}>
        {config.label}
      </Badge>
    );
  };

  return (
    <div className="space-y-4">
      {/* Actions Bar */}
      <div className="flex flex-wrap gap-2 items-center justify-between">
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={onSelectAll}>
            Selecionar Todos
          </Button>
          <Button variant="outline" size="sm" onClick={onDeselectAll}>
            Desmarcar Todos
          </Button>
          {duplicatesCount > 0 && (
            <Button variant="outline" size="sm" onClick={onDeselectDuplicates}>
              Ignorar Duplicatas ({duplicatesCount})
            </Button>
          )}
        </div>
        <p className="text-sm text-muted-foreground">
          {selectedCount} de {transactions.length} selecionadas
        </p>
      </div>

      {/* Table */}
      <div className="border rounded-lg overflow-hidden">
        <div className="max-h-[400px] overflow-y-auto">
          <table className="w-full">
            <thead className="bg-muted/50 sticky top-0">
              <tr className="text-left text-sm">
                <th className="p-3 w-10"></th>
                <th className="p-3">Data</th>
                <th className="p-3">Descrição</th>
                <th className="p-3">Valor</th>
                <th className="p-3">Categoria</th>
                <th className="p-3 w-24">Status</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map((tx) => (
                <tr 
                  key={tx.id}
                  className={cn(
                    "border-t transition-colors",
                    tx.selected ? "bg-background" : "bg-muted/30 opacity-60",
                    tx.is_duplicate && "bg-warning/5"
                  )}
                >
                  <td className="p-3">
                    <Checkbox
                      checked={tx.selected}
                      onCheckedChange={() => onToggle(tx.id)}
                    />
                  </td>
                  <td className="p-3 text-sm whitespace-nowrap">
                    {formatDate(tx.date)}
                  </td>
                  <td className="p-3">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <p className="text-sm truncate max-w-[200px]">
                            {tx.description}
                          </p>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{tx.description}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </td>
                  <td className="p-3">
                    <span className={cn(
                      "font-medium text-sm",
                      tx.type === 'income' ? 'text-success' : 'text-destructive'
                    )}>
                      {tx.type === 'income' ? '+' : '-'}{formatCurrency(tx.amount)}
                    </span>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center">
                      <CategorySelect
                        value={tx.category_id}
                        onChange={(categoryId, categoryName) => onCategoryChange(tx.id, categoryId, categoryName)}
                        type={tx.type}
                        placeholder="Selecionar..."
                        allowCreate={true}
                        triggerClassName="w-[160px] h-8 text-sm"
                      />
                      {getConfidenceBadge(tx.confidence, tx.auto_mapped)}
                    </div>
                  </td>
                  <td className="p-3">
                    <div className="flex gap-1">
                      {tx.is_duplicate && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger>
                              <Badge variant="outline" className="bg-warning/20 text-warning border-warning/30">
                                <AlertTriangle className="w-3 h-3 mr-1" />
                                Duplicata
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Similaridade: {tx.duplicate_similarity}%</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                      {tx.is_installment && (
                        <Badge variant="outline" className="bg-info/20 text-info border-info/30">
                          <Repeat className="w-3 h-3 mr-1" />
                          {tx.installment_info}
                        </Badge>
                      )}
                      {!tx.is_duplicate && !tx.is_installment && (
                        <Badge variant="outline" className="bg-success/20 text-success border-success/30">
                          <Check className="w-3 h-3 mr-1" />
                          Nova
                        </Badge>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
